import React, { useCallback, useContext, useMemo, useState, useEffect, useRef } from "react";
import { Card, Form, Modal } from "react-bootstrap";
import { TiDeleteOutline } from "react-icons/ti";
import SmuBadge from "../../utils/smu-badge/SmuBadge";
import SmuSearch from "../../utils/smu-search/SmuSearch";
import { COLOR_PRIMARY } from "../../constants/colors";
import { prepareText, resolveUserTienda } from "../../helpers";
import SmuSkirtCards from "../../utils/smu-skirt-cards/SmuSkirtCards";
import ProductsTable from "../products-table/ProductsTable";
import { useNavigate } from "react-router-dom";
import { StoreContext } from "../../store/StoreContext";
import { downloadFaldonXlsxWithCopies, getLatestDownloadFaldonStatus } from "../../api/files";
import { getStores } from "../../api/stores";
import { toast } from "../../helpers/smu-alerts/smu-lerts";
import { IconArrowLeft } from "../../icons/Icon";
import SmuTable from "../../utils/smu-table/SmuTable";
import { fetchDeletedProductsByPromo } from "../../api/promotions";
import loadingGif from "../../assets/images/loading.gif";
import SmuLoading from "../../utils/smu-loading/SmuLoading";
import { SMU_FILTER, SMU_PRESERVE_PROMOTION_FILTERS } from "../../constants";

// Mock test
// 1. uncomment lext lines (imports and alt component constructor)
// 2. Test with: http://localhost:3000/promotional-portal/portal/promotion/detail/54555555100
//import { skirtsDetailsArticles as DUMMY_PRODUCTS } from "../../api/dummy/skirts-details";
//import { promotionsV2025 as DUMMY_PROMOTIONS } from "../../api/dummy/promotions";

const ProductPromotionsTable = ({ products , promotionData, loading, filter = null, handleApplyFilters}) => {
//const ProductPromotionsTable = ({ products = DUMMY_PRODUCTS, promotionData = DUMMY_PROMOTIONS[0], loading, filter = null, handleApplyFilters}) => {
  //products = DUMMY_PRODUCTS;
  const promotionId = promotionData.ID_PROMO;
  const navigate = useNavigate();
  const [selectedFaldones, setSelectedFaldones] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const { loggedUser, isAdmin } = useContext(StoreContext);
  const [loadingPercent, setLoadingPercent] = useState(0);
  const [cantidadCopias, setCantidadCopias] = useState(1);
  const [skirtCardsKey, setSkirtCardsKey] = useState(0);
  const [tipoPagina, setTipoPagina] = useState({value: filter?.formato_impresion || "cuarto-pagina"});
  const [showDeletedModal, setShowDeletedModal] = useState(false);
  const [searchDeletedProducts, setSearchDeletedProducts] = useState("");
  const [deletedProducts, setDeletedProducts] = useState([]);
  const [loadingDeletedProducts, setLoadingDeletedProducts] = useState(false);
  const [appliedFilterCriteria, setAppliedFilterCriteria] = useState({});
  const [visibleSkirtCards, setVisibleSkirtCards] = useState([]);
  const [availableUploadedSkirtDates, setAvailableUploadedSkirtDates] = useState([]);
  
  // Filters state (simple in-memory storage)
  const [producto, setProducto] = useState("");
  const [marca, setMarca] = useState("");
  const [categoria, setCategoria] = useState("");
  const [ean, setEan] = useState(filter?.ean ? filter.ean : "");
  const [tiendaFilter, setTiendaFilter] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [allStores, setAllStores] = useState([]);
  const [adminStoreSearch, setAdminStoreSearch] = useState("");
  const [isAdminStoreDropdownOpen, setIsAdminStoreDropdownOpen] = useState(false);
  const adminDropdownRef = useRef(null);
  const adminSelectRef = useRef(null);

  // Date filters + UI helpers
  const [showDateFilters, setShowDateFilters] = useState(false);
  const [createdAtFrom, setCreatedAtFrom] = useState("");
  const [createdAtTo, setCreatedAtTo] = useState("");
  const [startDateFrom, setStartDateFrom] = useState("");
  const [startDateTo, setStartDateTo] = useState(() => {
    // Initialize with today's date
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });
  const [endDateFrom, setEndDateFrom] = useState(() => {
    // Initialize with today's date
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });
  const [endDateTo, setEndDateTo] = useState("");
  const [onlyFutureSkirts, setOnlyFutureSkirts] = useState(false);

  // Optional error messages (kept minimal for now)
  const [createdAtFromError, setCreatedAtFromError] = useState("");
  const [createdAtToError, setCreatedAtToError] = useState("");
  const [startDateFromError, setStartDateFromError] = useState("");
  const [startDateToError, setStartDateToError] = useState("");
  const [endDateFromError, setEndDateFromError] = useState("");
  const [endDateToError, setEndDateToError] = useState("");
  const [UploadedSkirtDate, setUploadedSkirtDate] = useState("");

  const filteredStores = useMemo(() => {
    const list = Array.isArray(allStores) ? allStores : [];
    const query = (adminStoreSearch || "").trim().toLowerCase();
    if (!query) return list;
    return list.filter((store) => {
      const code = String(store?.codLocal ?? store?.codigoLocal ?? store?.codigo ?? store?.id ?? "");
      const label = /^\d+$/.test(code) ? parseInt(code, 10).toString().padStart(4, "0") : code;
      const desc = String(store?.descripcion ?? store?.description ?? "").toLowerCase();
      return label.toLowerCase().includes(query) || desc.includes(query);
    });
  }, [allStores, adminStoreSearch]);

  // Get today's date in YYYY-MM-DD format for date restrictions
  const getTodayDateString = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Get tomorrow's date in YYYY-MM-DD format
  const getTomorrowDateString = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const year = tomorrow.getFullYear();
    const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
    const day = String(tomorrow.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDate = (date, extraDays = 0) => {
    let dateObj;
    if (date === '') {
      return null;
    } else if (typeof date === 'string') {
      // Accepts only DD-MM-YYYY formatted string
      const [day, month, year] = date.split('-');
      // Always use Date.UTC to avoid timezone offset issues when parsing "DD-MM-YYYY"
      dateObj = new Date(Date.UTC(parseInt(year), parseInt(month, 10) - 1, parseInt(day, 10)));
    } else {
      // If date is Date object, clone it in UTC
      dateObj = new Date(Date.UTC(
        date.getUTCFullYear(), 
        date.getUTCMonth(), 
        date.getUTCDate()
      ));
    }
    // Always operate in UTC to avoid timezone drift
    const finalDate = new Date(dateObj);
    finalDate.setUTCDate(finalDate.getUTCDate() + extraDays);
    const yearResult = finalDate.getUTCFullYear();
    const monthResult = String(finalDate.getUTCMonth() + 1).padStart(2, '0');
    const dayResult = String(finalDate.getUTCDate()).padStart(2, '0');
    return `${yearResult}-${monthResult}-${dayResult}`;
  };

  const todayDateString = useMemo(() => getTodayDateString(), []);

  useEffect(() => {
    if (availableUploadedSkirtDates.length > 0) return;

    if (!Array.isArray(products) || products.length === 0) {
      setAvailableUploadedSkirtDates([]);
      return;
    }

    const uniqueFormattedSortedDates = [
      ...new Set(
        products
          .filter(product => typeof product.fechaHoraCarga === 'string' && product.fechaHoraCarga.includes('T'))
          .map(product => product.fechaHoraCarga.split('T')[0])
      ),
    ]
      .sort((a, b) => b.localeCompare(a))
      .map(date => {
        const [year, month, day] = date.split('-');
        return `${day}-${month}-${year}`;
      });
  
    setAvailableUploadedSkirtDates(uniqueFormattedSortedDates);
  }, [products]);

  const getAdminSelectedStoreDisplay = () => {
    if (!tiendaFilter) return "Todas";
    const code = String(tiendaFilter);
    return /^\d+$/.test(code) ? parseInt(code, 10).toString().padStart(4, "0") : code;
  };
  
  useEffect(() => {
      // clean filters only if the formato_impresion is different
      var filtro = JSON.parse(localStorage.getItem(SMU_FILTER));
      if(filtro?.formato_impresion !== tipoPagina.value) {
        filtro.formato_impresion = tipoPagina.value;
        filtro.ean = "";
        localStorage.setItem(SMU_FILTER, JSON.stringify(filtro));
        setProducto("");
        setMarca("");
        setCategoria("");
        setEan("");
        setTiendaFilter("");
        setCreatedAtFrom("");
        setCreatedAtTo("");
        setStartDateFrom("");
        setStartDateTo(getTodayDateString());
        setEndDateFrom(getTodayDateString());
        setEndDateTo("");
        setOnlyFutureSkirts(false);
        setAppliedFilterCriteria({});
        handleFilter();
      }
    }, [tipoPagina]);

  useEffect(() => {
    if (!isAdmin) {
      setAllStores([]);
      return;
    }
    getStores()
      .then(({ data }) => setAllStores(data || []))
      .catch(() => setAllStores([]));
  }, [isAdmin]);

  useEffect(() => {
    if (onlyFutureSkirts) {
      // Set "Fecha de inicio (desde)" to tomorrow and clear "Fecha de inicio (hasta)" and "Fecha de fin (desde)"
      setStartDateFrom(getTomorrowDateString());
      setStartDateTo("");
      setEndDateFrom("");
      setEndDateTo("");
    } else {
      // Restore default: clear "Fecha de inicio (desde)", set "Fecha de inicio (hasta)" to today, and "Fecha de fin (desde)" to today
      setStartDateFrom("");
      setStartDateTo(getTodayDateString());
      setEndDateFrom(getTodayDateString());
      setEndDateTo("");
    }
  }, [onlyFutureSkirts]);

  const handleDateChange = (setValue, setError, value) => {
    setValue(value);
    setError("");
  };

  const handleFilter = () => {
    const payload = {
      producto: producto,
      marca: marca,
      categoria: categoria,
      eans: [ean],
      tienda: tiendaFilter,
      created_at_from: createdAtFrom,
      created_at_to: createdAtTo,
      start_date_from: startDateFrom,
      start_date_to: startDateTo,
      end_date_from: endDateFrom,
      end_date_to: endDateTo,
      only_future_skirts: onlyFutureSkirts,
      formato_impresion: tipoPagina.value,
      uploaded_skirt_date: UploadedSkirtDate,
    };
    // Save the criteria that were actually applied (for UI behaviors like showing EAN from the first match)
    setAppliedFilterCriteria({
      producto,
      marca,
      categoria,
      ean,
    });
    const filtro = JSON.parse(localStorage.getItem(SMU_FILTER));
    if(ean) {
      filtro.ean = ean;
    }
    localStorage.setItem(SMU_FILTER, JSON.stringify(filtro));
    // For now, only log the collected filters
    // eslint-disable-next-line no-console
    handleApplyFilters(payload);
  };

  const handleSelectAll = () => {
    // Use filteredSkirtItems to match what's actually visible in SmuSkirtCards
    let visibleFaldones = [];
    if(toggleSwitch){
      // The visualization is the skirt cards, so visibleFaldones is different
      visibleFaldones = visibleSkirtCards.map(g => g.skirt_name);
    } else {
      visibleFaldones = filteredSkirtItems.map(g => g.skirt_name);
    }
    const allSelected = visibleFaldones.length > 0 && visibleFaldones.every(f => selectedFaldones.includes(f));
    if (allSelected) {
      setSelectedFaldones(prev => {
        const next = prev.filter(f => !visibleFaldones.includes(f));
        setSkirtCardsKey(k => k + 1);
        return next;
      });
    } else {
      setSelectedFaldones(prev => {
        const set = new Set(prev);
        visibleFaldones.forEach(f => set.add(f));
        const next = Array.from(set);
        setSkirtCardsKey(k => k + 1);
        return next;
      });
    }
  };

  const handleDownloadSelected = async () => {
    const username = loggedUser?.username;
    const tienda = tiendaFilter ? tiendaFilter : resolveUserTienda(isAdmin, loggedUser);
    setDownloading(true);
    setLoadingPercent(0);

    const statusToMax = {FILTERING: 40, RPA_VIRAL: 94, SUCCESS: 100, FAILURE: 100};
    const statusBase = { FILTERING: 1, RPA_VIRAL: 41, SUCCESS: 95, FAILURE: 95 };

    // Progress increment
    let progress = 0;
    let maxDuringDownload = statusToMax.FILTERING;
    const intervalDelay = 150;
    let randomDelayGivenStatus = 150;
    let progressInterval;
    const runProgressCycle = () => {
      randomDelayGivenStatus = progress < statusBase.RPA_VIRAL
        ? Math.floor(Math.random() * 350)
        : Math.floor(Math.random() * 350);
      const intervalStep = Math.floor(Math.random() * (progress < statusBase.RPA_VIRAL ? 3 : 3));
      progress = Math.min(progress + intervalStep, maxDuringDownload);
      setLoadingPercent(progress);
      progressInterval = setTimeout(runProgressCycle, intervalDelay + randomDelayGivenStatus);
    };
    runProgressCycle();
  
    // Polling download status every 7s
    const pollingInterval = setInterval(async () => {
      try {
        if(progress>statusToMax.FILTERING){return}
        const downloadId = promotionId
          + (tipoPagina.value ? ("::" + tipoPagina.value.toLowerCase()) : "") 
          + (promotionData.NEGOCIO ? ("::" + promotionData.NEGOCIO.toUpperCase()) : "") 
          + (promotionData.NOMBRE_CAMPANA ? ("::" + promotionData.NOMBRE_CAMPANA.toLowerCase()) : "");
        const statusResp = await getLatestDownloadFaldonStatus(downloadId, username);
        const newMax = statusToMax[statusResp.REQUEST_STATUS] ?? maxDuringDownload;
        if (newMax > maxDuringDownload) {
          maxDuringDownload = newMax;
          progress = statusBase[statusResp.REQUEST_STATUS];
          setLoadingPercent(progress);
        }
      } catch (err) {
        console.warn("status fetch failed.");
      }
    }, 7000);

    try{
      var faldones = [];
      if(selectedFaldones.length === 1){
        // find the faldon in the products array and from the first entry found get the version
        const version = products.find(p => p.faldon === selectedFaldones[0])?.version;
        faldones.push(`${selectedFaldones[0]}|${cantidadCopias}|${version ? version : 1}`);
      } else {
        faldones = selectedFaldones.map(f => {
          const version = products.find(p => p.faldon === f)?.version;
          return `${f}|1|${version ? version : 1}`;
        });
      }
      const response = await downloadFaldonXlsxWithCopies(promotionId, {tienda, username, faldones, negocio: promotionData.NEGOCIO, formatoImpresion: tipoPagina.value, nombreCampana: promotionData.NOMBRE_CAMPANA}); // TODO: agregar category_menu al params
      //const response = await downloadFaldonXlsx(faldonId, { tienda, username, products: selectedProductIds });

      clearInterval(progressInterval);
      clearInterval(pollingInterval);
      setLoadingPercent(97); // response arrived

      // 204 - (no elements for filtered faldon)
      if (response.status === 204) {
        toast({ icon: "info", title: "No hay eans que coincidan el faldón filtrado" });
        setLoadingPercent(100);
        setDownloading(false);
        return;
      }

      let downloadFilename = promotionId.replace(/\.(xlsx|xls)$/i, '.pdf');
      const disposition = response.headers['content-disposition'] || response.headers['Content-Disposition'];
      if (disposition) {
        const match = disposition.match(/filename=\"?([^\";]+)\"?/);
        if (match && match[1]) {
          downloadFilename = match[1];
        }
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', downloadFilename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast({ icon: "success", title: "Descarga iniciada" });
      setSelectedFaldones([]);
      setSkirtCardsKey(k => k + 1);
      setLoadingPercent(99);

    } catch (err) {
      clearInterval(progressInterval);
      clearInterval(pollingInterval);
      toast({ icon: "error", title: "Error al descargar" });
    }

    setLoadingPercent(100);
    setDownloading(false);
  };

  // handler
  const handleShowDeletedProducts = async () => {
    setShowDeletedModal(true);
    setLoadingDeletedProducts(true);
    
    try {
      // Get stores from loggedUser.store (array)
      const stores = loggedUser?.store || [];
      // Join stores with comma for p_local parameter (or use -1 if admin)
      const p_local = isAdmin 
        ? -1 
        : stores.length > 0 
          ? (stores.length === 1 ? stores[0] : stores.join(","))
          : -1;
      
      let promotionParam = promotionId ? String(promotionId) : "";
      if (promotionParam.length > 0 && promotionParam.length < 18) {
        promotionParam = promotionParam.padStart(18, "0");
      }

      const negocio = promotionData.NEGOCIO ? promotionData.NEGOCIO.toUpperCase() : null;
      const nombreCampana = promotionData.NOMBRE_CAMPANA ? promotionData.NOMBRE_CAMPANA.toUpperCase() : null;

      const response = await fetchDeletedProductsByPromo(promotionParam, p_local, negocio, nombreCampana);
      setDeletedProducts(response?.data || []);
    } catch (error) {
      console.error("Error fetching deleted products:", error);
      toast({ icon: "error", title: "Error al cargar productos eliminados" });
      setDeletedProducts([]);
    } finally {
      setLoadingDeletedProducts(false);
    }
  };

  const deletedProductsColumns = useMemo(
    () => [
      {
        id: "PRODUCTO",
        name: "Producto",
        selector: (row) => row.PRODUCTO,
        cell: (row) => <div className="cell">{row.PRODUCTO}</div>,
        sortable: true,
      },
      {
        id: "FECHA_HORA_CREACION",
        name: "Fecha Actualización",
        selector: (row) => row.FECHA_HORA_CREACION,
        cell: (row) => <div className="cell">{new Date(row.FECHA_HORA_CREACION).toLocaleString()}</div>,
        sortable: true,
      },
      {
        id: "EAN11",
        name: "EAN",
        selector: (row) => row.EAN11,
        cell: (row) => <div className="cell">{row.EAN11}</div>,
        sortable: true,
      },
      {
        id: "WERKS",
        name: "Tienda",
        selector: (row) => row.WERKS,
        cell: (row) => <div className="cell">{row.WERKS}</div>,
        sortable: true,
      },
      {
        id: "IDPROM",
        name: "Cod. Promoción",
        selector: (row) => row.IDPROM,
        cell: (row) => <div className="cell">{row.IDPROM}</div>,
        sortable: true,
      },
      /*
      {
        id: "ID_SURTIDO",
        name: "ID Surtido",
        selector: (row) => row.ID_SURTIDO,
        cell: (row) => <div className="cell">{row.ID_SURTIDO}</div>,
        sortable: true,
      },
      
      {
        id: "MATNR",
        name: "MATNR",
        selector: (row) => row.MATNR,
        cell: (row) => <div className="cell">{row.MATNR}</div>,
        sortable: true,
      },
      {
        id: "LOEVM",
        name: "Eliminado",
        selector: (row) => row.LOEVM,
        cell: (row) => <div className="cell">{row.LOEVM === "X" ? "Sí" : "No"}</div>,
        sortable: true,
      },
      */
    ],
    []
  );

  const [search, setSearch] = useState("");
  const [toggleSwitch, setToggleSwitch] = useState(true);
  const prevToggleSwitchRef = useRef(toggleSwitch);

  // Clear filters when toggling between views (except formato_impresion)
  // BUT preserve filters if navigating between ProductPromotionsTable and ProductsTable
  useEffect(() => {
    // Only clear if toggleSwitch actually changed (not on initial mount)
    if (prevToggleSwitchRef.current === toggleSwitch) return;
    prevToggleSwitchRef.current = toggleSwitch;
    
    // Check if we should preserve filters (navigating between ProductPromotionsTable and ProductsTable)
    const preserveFlag = localStorage.getItem(SMU_PRESERVE_PROMOTION_FILTERS);
    if (preserveFlag === "true") {
      // Don't clear filters - preserve them for navigation between these views
      return;
    }
    
    const currentFilter = JSON.parse(localStorage.getItem(SMU_FILTER) || '{}');
    const formatoImpresion = currentFilter?.formato_impresion || "cuarto-pagina";
    // Reset all filter values to "" while preserving formato_impresion
    const clearedFilter = {
      tienda: "",
      ean: "",
      f_categoria: "",
      f_marca: "",
      f_producto: "",
      fecha_publicacion_desde: "",
      fecha_publicacion_hasta: "",
      fecha_inicio_desde: "",
      fecha_inicio_hasta: "",
      fecha_fin_desde: "",
      fecha_fin_hasta: "",
      formato_impresion: formatoImpresion,
      keyword: "",
      p_nombre_producto: "",
      p_seccion: "",
      p_linea: "",
      p_categoria: "",
      f_negocio: ""
    };
    localStorage.setItem(SMU_FILTER, JSON.stringify(clearedFilter));
    // Also clear local filter states and search
    setProducto("");
    setMarca("");
    setCategoria("");
    setEan("");
    setTiendaFilter("");
    setCreatedAtFrom("");
    setCreatedAtTo("");
    setStartDateFrom("");
    setStartDateTo(getTodayDateString());
    setEndDateFrom(getTodayDateString());
    setEndDateTo("");
    setOnlyFutureSkirts(false);
    setAppliedFilterCriteria({});
    setSearch(""); // Clear search state when toggling
  }, [toggleSwitch]);

  const filteredDeletedProducts = useMemo(() => {
    const _search = prepareText(searchDeletedProducts.trim());
    if (!_search) return deletedProducts;
    
    return deletedProducts.filter((p) => {
      const _producto = prepareText(p.PRODUCTO || "");
      const _ean = prepareText(p.EAN11 || "");
      const _tienda = prepareText(p.WERKS?.toString() || "");
      const _idprom = prepareText(p.IDPROM || "");
      const _fecha = prepareText(new Date(p.FECHA_HORA_CREACION).toLocaleString() || "");
      
      return (
        _producto.includes(_search) ||
        _ean.includes(_search) ||
        _tienda.includes(_search) ||
        _idprom.includes(_search) ||
        _fecha.includes(_search)
      );
    });
  }, [searchDeletedProducts, deletedProducts]);

  const filteredProducts = useMemo(() => {
    const _search = prepareText(search.trim());
    
    if(!Array.isArray(products) || products.length === 0) return [];
    return products.filter((p) => {
      const _faldon = prepareText(p.faldon);
      const _producto = prepareText(p.producto);
      const _marca = prepareText(p.marca || "N/A");
      const _categoria = prepareText(p.categoria || "N/A");
      const _ean = prepareText(p.ean13 || "N/A");
      
      return (
        _faldon.includes(_search) ||
        _producto.includes(_search) ||
        _marca.includes(_search) ||
        _categoria.includes(_search) ||
        _ean.includes(_search)
      );
    });
  }, [search, products]);

  const skirtItems = useMemo(() => {
    const map = new Map();
    (products || []).forEach((p) => {
      const faldon = p.faldon;
      if (!map.has(faldon)) map.set(faldon, []);
      // Ensure modal columns in SmuSkirtCards can read 'faldonXlsx'
      map.get(faldon).push({ ...p, faldonXlsx: p.faldon });
    });
    const getLatestTimestamp = (list = []) =>
      list.reduce((latest, product) => {
        if (!product?.fechaHoraCarga) return latest;
        const timestamp = new Date(product.fechaHoraCarga).getTime();
        return Number.isNaN(timestamp) ? latest : Math.max(latest, timestamp);
      }, 0);
    return Array.from(map.entries())
      .map(([faldon, prods]) => ({
        skirt_name: faldon,
        product_count: prods.length,
        products: prods,
      }))
      .sort(
        (a, b) => getLatestTimestamp(b.products) - getLatestTimestamp(a.products)
      );
  }, [products]);

  // Filtered skirt items based on search (matching SmuSkirtCards filtering logic)
  const filteredSkirtItems = useMemo(() => {
    const term = prepareText((search || "").trim());
    if (!term) return skirtItems;
    return skirtItems.filter((group) => {
      // Check faldon (skirt_name)
      const _faldon = prepareText(group.skirt_name || "");
      if (_faldon.includes(term)) return true;
      
      // Check product_count
      const _productCount = prepareText(String(group.product_count || ""));
      if (_productCount.includes(term)) return true;
      
      return false;
    });
  }, [skirtItems, search]);

  const handleSelectionChange = useCallback(
    (selectedItems) => {
      setSelectedFaldones(selectedItems.map((g) => g.skirt_name));
    },
    []
  );

  // When toggle is off, render ProductsTable instead
  if (!toggleSwitch) {
    // Read fresh filter from localStorage to ensure it's cleared
    const currentFilter = JSON.parse(localStorage.getItem(SMU_FILTER) || '{}');
    return (
      <ProductsTable
        key={`products-table-${toggleSwitch}`}
        products={products}
        faldonId={promotionId}
        loading={loading}
        filter={currentFilter}
        toggleSwitch={toggleSwitch}
        onToggleSwitchChange={(value) => setToggleSwitch(value)}
      />
    );
  }

  return (
    <Card className="smu-card">
      <Card.Header className="d-flex py-4">
        <div className="d-flex align-items-center me-auto">
          <SmuBadge color="#B1E5FC" />
          <span className="fs-5 ms-3 me-auto">ARTICULOS</span>
          <Form.Select
            value={tipoPagina.value}
            className="ms-3"
            onChange={(e) => setTipoPagina({name: e.target.value, value: e.target.value})}
          >
            <option value="cuarto-pagina">Cuarto de página</option>
            <option value="pagina-completa">Página completa</option>
            <option value="pagina-completa-horizontal">Página completa horizontal</option>
            <option value="media-pagina">Media página</option>
          </Form.Select>
          
        </div>
        {/* <div className="d-flex align-items-center">
          <SmuSearch
            value={search}
            onChange={({ target }) => setSearch(target.value)}
          />
        </div> */}
        <button
          className="smu-generic-button my-auto py-1 fs-6 px-3 d-flex align-items-center ms-4"
          onClick={() => {
            // Clear the preserve flag when navigating back to PromotionsTable
            localStorage.removeItem(SMU_PRESERVE_PROMOTION_FILTERS);
            navigate(-1);
          }}
        >
          <IconArrowLeft />
          <span className="ms-2">Atras</span>
        </button>
      </Card.Header>
      <div className="px-3 d-flex mb-3">
        <strong className="me-2" style={{ color: COLOR_PRIMARY }}>
          Promoción:
        </strong>
        {promotionId.replace(/\.(xlsx|xls)$/i, '')} ({promotionData.NEGOCIO}) - {promotionData.DESC_PROMO} ({promotionData.NOMBRE_CAMPANA})

      </div>

      {/* Filters - modeled after NewSkirtTable.jsx */}
      <div className="px-3">
        {/* Toggle button for filters */}
        <div className="row g-3 align-items-end mb-3">
          <div className="col-12">
            <button
              type="button"
              className="btn btn-link p-0 text-decoration-none d-flex align-items-center"
              onClick={() => setShowFilters(!showFilters)}
              style={{
                color: '#6c757d',
                fontSize: '14px',
                border: 'none',
                background: 'none'
              }}
            >
              <span className="me-2">
                {showFilters ? 'v' : '>'}
              </span>
              {showFilters ? 'Ocultar filtros' : 'Mostrar filtros'}
            </button>
          </div>
        </div>

        <div className="row g-3 align-items-end">
          {/* Filters section - conditionally rendered */}
          {showFilters && (
            <>
          <div className="col-md-2">
            <Form.Group>
              <Form.Label className="text-muted small mb-1">Producto</Form.Label>
              <Form.Control
                type="text"
                placeholder="Ingrese producto"
                value={producto}
                maxLength={101}
                onChange={(e) => setProducto(e.target.value.slice(0, 100))}
                style={{
                  backgroundColor: '#f4f4f4',
                  borderRadius: '12px',
                  border: 'none',
                  height: '44px'
                }}
              />
            </Form.Group>
          </div>

          <div className="col-md-2">
            <Form.Group>
              <Form.Label className="text-muted small mb-1">Marca</Form.Label>
              <Form.Control
                type="text"
                placeholder="Ingrese marca"
                value={marca}
                maxLength={101}
                onChange={(e) => setMarca(e.target.value.slice(0, 100))}
                style={{
                  backgroundColor: '#f4f4f4',
                  borderRadius: '12px',
                  border: 'none',
                  height: '44px'
                }}
              />
            </Form.Group>
          </div>

          <div className="col-md-2">
            <Form.Group>
              <Form.Label className="text-muted small mb-1">Categoría</Form.Label>
              <Form.Control
                type="text"
                placeholder="Ingrese categoría"
                value={categoria}
                maxLength={101}
                onChange={(e) => setCategoria(e.target.value.slice(0, 100))}
                style={{
                  backgroundColor: '#f4f4f4',
                  borderRadius: '12px',
                  border: 'none',
                  height: '44px'
                }}
              />
            </Form.Group>
          </div>

          <div className="col-md-2">
            <Form.Group>
              <Form.Label className="text-muted small mb-1">EAN 13</Form.Label>
              <Form.Control
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="Ingrese EAN"
                value={ean}
                maxLength={101}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9,]/g, '').slice(0, 100);
                  setEan(value);
                }}
                style={{
                  backgroundColor: '#f4f4f4',
                  borderRadius: '12px',
                  border: 'none',
                  height: '44px'
                }}
              />
            </Form.Group>
          </div>

          {/* Tienda */}
          {!isAdmin && loggedUser && loggedUser.store && loggedUser.store.length > 1 && (
            <div className="col-md-2">
              <Form.Group>
                <Form.Label className="text-muted small mb-1">Tienda</Form.Label>
                <Form.Select
                  value={tiendaFilter ?? ""}
                  onChange={(e) => setTiendaFilter(e.target.value)}
                  style={{
                    backgroundColor: '#f4f4f4',
                    borderRadius: '12px',
                    border: 'none',
                    height: '44px'
                  }}
                >
                  <option value="">Seleccionar tienda</option>
                  {(loggedUser.store || []).map((store) => (
                    <option key={store} value={store}>
                      {parseInt(store).toString().padStart(4, '0')}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>
          )}

          {isAdmin && (
            <div className="col-md-2">
              <Form.Group>
                <Form.Label className="text-muted small mb-1">Tienda</Form.Label>
                <div className="position-relative">
                  <Form.Control
                    type="text"
                    className="text-dark"
                    placeholder={getAdminSelectedStoreDisplay()}
                    value={isAdminStoreDropdownOpen ? adminStoreSearch : getAdminSelectedStoreDisplay()}
                    onChange={(e) => {
                      setAdminStoreSearch(e.target.value);
                      setIsAdminStoreDropdownOpen(true);
                    }}
                    onFocus={() => setIsAdminStoreDropdownOpen(true)}
                    onBlur={(e) => {
                      if (adminDropdownRef.current && adminDropdownRef.current.contains(e.relatedTarget)) {
                        return;
                      }
                      setTimeout(() => setIsAdminStoreDropdownOpen(false), 200);
                    }}
                    style={{
                      backgroundColor: '#f4f4f4',
                      borderRadius: '12px',
                      border: 'none',
                      height: '44px'
                    }}
                    autoComplete="off"
                    ref={adminSelectRef}
                  />
                  {isAdminStoreDropdownOpen && (
                    <div
                      ref={adminDropdownRef}
                      className="position-absolute w-100 bg-white border border-secondary rounded mt-1"
                      style={{ zIndex: 1000, maxHeight: '240px', overflowY: 'auto' }}
                      onMouseDown={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const scrollbarWidth = 17;
                        const isClickingOnScrollbar = e.clientX > rect.right - scrollbarWidth;
                        if (isClickingOnScrollbar) {
                          e.preventDefault();
                          e.stopPropagation();
                        }
                      }}
                    >
                      <div
                        className="px-3 py-2 cursor-pointer border-bottom"
                        style={{ cursor: 'pointer' }}
                        onMouseDown={() => {
                          setTiendaFilter("");
                          setAdminStoreSearch('');
                          setIsAdminStoreDropdownOpen(false);
                        }}
                      >
                        Todas
                      </div>
                      {(Array.isArray(filteredStores) ? filteredStores : []).map((store) => {
                        const code = String(store?.codLocal ?? store?.codigoLocal ?? store?.codigo ?? store?.id ?? "");
                        const label = /^\d+$/.test(code) ? parseInt(code, 10).toString().padStart(4, '0') : code;
                        return (
                          <div
                            key={code}
                            className="px-3 py-2 cursor-pointer border-bottom"
                            style={{ cursor: 'pointer' }}
                            onMouseDown={() => {
                              setTiendaFilter(code);
                              setAdminStoreSearch('');
                              setIsAdminStoreDropdownOpen(false);
                            }}
                          >
                            {label}
                          </div>
                        );
                      })}
                      {filteredStores.length === 0 && adminStoreSearch && (
                        <div className="px-3 py-2 text-muted">No se encontraron tiendas</div>
                      )}
                    </div>
                  )}
                </div>
              </Form.Group>
            </div>
          )}

          <div className="col-md-2 d-flex justify-content-end">
            <button
              className="ms-auto smu-generic-button solid-btn py-1"
              onClick={handleFilter}
              style={{ height: '44px', width: '100px' }}
            >
              Filtrar
            </button>
          </div>

          {/* Selector de grupo de subida (fecha de carga) */}
          <div className="col-md-2">
            <Form.Group>
              <Form.Label className="text-muted small mb-1">Fecha de carga</Form.Label>
              <Form.Select
                value={UploadedSkirtDate || ""}
                onChange={(e) => {
                  setUploadedSkirtDate(e.target.value);
                  handleDateChange(setCreatedAtFrom, setCreatedAtFromError, formatDate(e.target.value, 0));
                  handleDateChange(setCreatedAtTo, setCreatedAtToError, formatDate(e.target.value, 1));
                }}
                style={{
                  backgroundColor: '#f4f4f4',
                  borderRadius: '12px',
                  border: 'none',
                  height: '44px'
                }}
              >
                <option value="">Todas</option>
                {availableUploadedSkirtDates.map(date => (
                  <option key={date} value={date}>{date}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </div>
            </>
          )}
        </div>

        <div className="row g-3 align-items-end">
          {/* Toggle button for date filters */}
          <div className="col-12 mb-3">
            <button
              type="button"
              className="btn btn-link p-0 text-decoration-none d-flex align-items-center"
              onClick={() => setShowDateFilters(!showDateFilters)}
              style={{
                color: '#6c757d',
                fontSize: '14px',
                border: 'none',
                background: 'none'
              }}
            >
              <span className="me-2">
                {showDateFilters ? 'v' : '>'}
              </span>
              Filtrar fechas
            </button>
          </div>

          {/* Date filters section - conditionally rendered */}
          {showDateFilters && (
            <>
              {/* Publicación range */}
              <div className="col-md-3">
                <Form.Group>
                  <Form.Label className="text-muted small mb-1">Fecha de publicación (desde)</Form.Label>
                  <Form.Control
                    type="date"
                    value={createdAtFrom || ""}
                    onChange={(e) => handleDateChange(setCreatedAtFrom, setCreatedAtFromError, e.target.value)}
                    max={!onlyFutureSkirts ? todayDateString : undefined}
                    min={onlyFutureSkirts ? todayDateString : undefined}
                    style={{
                      backgroundColor: '#f4f4f4',
                      borderRadius: '12px',
                      border: createdAtFromError ? '1px solid #dc3545' : 'none',
                      height: '44px'
                    }}
                  />
                  {createdAtFromError && (
                    <div className="text-danger small mt-1">{createdAtFromError}</div>
                  )}
                </Form.Group>
                <Form.Group className="mt-1">
                  <Form.Label className="text-muted small mb-1">Fecha de publicación (hasta)</Form.Label>
                  <Form.Control
                    type="date"
                    value={createdAtTo || ""}
                    onChange={(e) => handleDateChange(setCreatedAtTo, setCreatedAtToError, e.target.value)}
                    max={!onlyFutureSkirts ? todayDateString : undefined}
                    min={onlyFutureSkirts ? todayDateString : undefined}
                    style={{
                      backgroundColor: '#f4f4f4',
                      borderRadius: '12px',
                      border: createdAtToError ? '1px solid #dc3545' : 'none',
                      height: '44px'
                    }}
                  />
                  {createdAtToError && (
                    <div className="text-danger small mt-1">{createdAtToError}</div>
                  )}
                </Form.Group>
              </div>

              {/* Inicio range */}
              <div className="col-md-3">
                <Form.Group>
                  <Form.Label className="text-muted small mb-1">Fecha de inicio (desde)</Form.Label>
                  <Form.Control
                    type="date"
                    value={startDateFrom || ""}
                    onChange={(e) => handleDateChange(setStartDateFrom, setStartDateFromError, e.target.value)}
                    max={!onlyFutureSkirts ? todayDateString : undefined}
                    min={onlyFutureSkirts ? todayDateString : undefined}
                    style={{
                      backgroundColor: '#f4f4f4',
                      borderRadius: '12px',
                      border: startDateFromError ? '1px solid #dc3545' : 'none',
                      height: '44px'
                    }}
                  />
                  {startDateFromError && (
                    <div className="text-danger small mt-1">{startDateFromError}</div>
                  )}
                </Form.Group>
                <Form.Group className="mt-1">
                  <Form.Label className="text-muted small mb-1">Fecha de inicio (hasta)</Form.Label>
                  <Form.Control
                    type="date"
                    value={startDateTo || ""}
                    onChange={(e) => handleDateChange(setStartDateTo, setStartDateToError, e.target.value)}
                    max={!onlyFutureSkirts ? todayDateString : undefined}
                    min={onlyFutureSkirts ? todayDateString : undefined}
                    style={{
                      backgroundColor: '#f4f4f4',
                      borderRadius: '12px',
                      border: startDateToError ? '1px solid #dc3545' : 'none',
                      height: '44px'
                    }}
                  />
                  {startDateToError && (
                    <div className="text-danger small mt-1">{startDateToError}</div>
                  )}
                </Form.Group>
              </div>

              {/* Fin range */}
              <div className="col-md-3">
                <Form.Group>
                  <Form.Label className="text-muted small mb-1">Fecha de fin (desde)</Form.Label>
                  <Form.Control
                    type="date"
                    value={endDateFrom || ""}
                    onChange={(e) => handleDateChange(setEndDateFrom, setEndDateFromError, e.target.value)}
                    max={!onlyFutureSkirts ? todayDateString : undefined}
                    min={onlyFutureSkirts ? todayDateString : undefined}
                    style={{
                      backgroundColor: '#f4f4f4',
                      borderRadius: '12px',
                      border: endDateFromError ? '1px solid #dc3545' : 'none',
                      height: '44px'
                    }}
                  />
                  {endDateFromError && (
                    <div className="text-danger small mt-1">{endDateFromError}</div>
                  )}
                </Form.Group>
                <Form.Group className="mt-1">
                  <Form.Label className="text-muted small mb-1">Fecha de fin (hasta)</Form.Label>
                  <Form.Control
                    type="date"
                    value={endDateTo || ""}
                    onChange={(e) => handleDateChange(setEndDateTo, setEndDateToError, e.target.value)}
                    max={!onlyFutureSkirts ? todayDateString : undefined}
                    min={onlyFutureSkirts ? todayDateString : undefined}
                    style={{
                      backgroundColor: '#f4f4f4',
                      borderRadius: '12px',
                      border: endDateToError ? '1px solid #dc3545' : 'none',
                      height: '44px'
                    }}
                  />
                  {endDateToError && (
                    <div className="text-danger small mt-1">{endDateToError}</div>
                  )}
                </Form.Group>
              </div>

              {/* Sólo faldones futuros */}
              <div className="col-md-3">
                <Form.Group>
                  <Form.Check
                    type="checkbox"
                    id="onlyFutureSkirts"
                    label="Sólo faldones futuros"
                    checked={onlyFutureSkirts}
                    onChange={(e) => setOnlyFutureSkirts(e.target.checked)}
                  />
                </Form.Group>
              </div>
            </>
          )}
        </div>
      </div>
      <div className="d-flex justify-content-between align-items-center gap-2 mt-3">
          <div className="d-flex align-items-center gap-2">
            <label htmlFor="custom-toggle-switch" className="mb-0" style={{ cursor: "pointer" }}>
              Visualización Nueva
            </label>
            <Form.Switch
              id="custom-toggle-switch"
              checked={toggleSwitch}
              onChange={(e) => setToggleSwitch(e.target.checked)}
            />
          </div>
          <div className="d-flex gap-2 align-items-center">
            {selectedFaldones.length === 1 && (
              <div className="d-flex align-items-center">
                <label htmlFor="cantidadCopias" className="me-2 mb-0" style={{ fontWeight: 500 }}>
                  Cantidad de copias:
                </label>
                <input
                  id="cantidadCopias"
                  type="number"
                  min={1}
                  max={100}
                  step={1}
                  value={typeof cantidadCopias !== "undefined" ? cantidadCopias : 1}
                  onChange={e => {
                    const val = parseInt(e.target.value, 10);
                    if (!isNaN(val) && val > 0) {
                      const valueToSet = val > 100 ? 100 : val;
                      if (typeof setCantidadCopias === "function") {
                        setCantidadCopias(valueToSet);
                      }
                    }
                  }}
                  style={{ width: 60 }}
                />
              </div>
            )}
            <button
              className="btn btn-success btn-sm border-0"
              onClick={handleDownloadSelected}
              disabled={selectedFaldones.length === 0 || downloading}
            >
              {downloading ? `Descargando... ${loadingPercent}%` : `Descargar seleccionados (${selectedFaldones.length})`}
            </button>
            <button
              className="btn btn-secondary btn-sm border-0"
              onClick={handleSelectAll}
              disabled={downloading}
            >
              Marcar / Desmarcar
            </button>
          </div>
        </div>
      <Card.Body>
        { !loading ? 
          (<SmuSkirtCards
            key={skirtCardsKey}
            items={skirtItems}
            filter={search}
            onFilterChange={(text) => setSearch(text)}
            defaultSelected={selectedFaldones}
            promotionId={promotionId}
            onSelectionChange={handleSelectionChange}
            // NOTE: filterCriteria temporarily disabled to avoid live filtering
            // while typing. Filters now only apply when clicking "Filtrar".
            // filterCriteria={{
            //   producto: producto,
            //   marca: marca,
            //   categoria: categoria,
            //   ean: ean,
            // }}
            // Criteria applied via "Filtrar" (not while typing) so EAN shows the first matching product
            filterCriteria={appliedFilterCriteria}
            setVisibleFaldones={setVisibleSkirtCards}
          />)
          : ( <SmuLoading label="Cargando faldones..." /> )
        }
      </Card.Body>
      {/* extended downloading indicator - split based in statusBase*/}
      {downloading && (
        <span
          className="text-muted small"
          style={{
            position: "fixed", borderRadius: "8px", padding: "8px 16px",
            bottom: 16, right: 24, zIndex: 9999,
            background: "rgba(255,255,255,0.95)", boxShadow: "0 2px 8px rgba(0,0,0,0.38)"
          }}
        >{loadingPercent <= 40 ? "1/2 Filtrando artículos de faldón..." : "2/2 Generando faldón PDF..."}</span>
      )}
      {/* Floating button: Productos eliminados */}
      <button
        type="button"
        className="smu-generic-button my-auto py-1 fs-6 px-3 d-flex align-items-center solid-btn"
        style={{
          position: "fixed",
          bottom: downloading ? 64 : 16,
          right: 24,
          zIndex: 10000,
          borderRadius: "20px",
          padding: "8px 12px"
        }}
        onClick={handleShowDeletedProducts}
        disabled={loadingDeletedProducts}
      >
        {loadingDeletedProducts ? (
          <img src={loadingGif} alt="Cargando" style={{ width: 18, height: 18 }} />
        ) : (
          <TiDeleteOutline style={{ width: 18, height: 18 }} />
        )}
        <span className="ms-2" title="Productos eliminados de la campaña y negocio seleccionado">Productos eliminados</span>
      </button>
      {/* Modal placeholder for deleted products */}
      <Modal
        show={showDeletedModal}
        onHide={() => {
          setShowDeletedModal(false);
          setSearchDeletedProducts("");
          setDeletedProducts([]);
        }}
        size="xl"
        centered
        scrollable height="80vh"
      >
        <Modal.Header closeButton className="d-flex align-items-center">
          <Modal.Title className="me-auto">Productos eliminados</Modal.Title>
          <div className="d-flex align-items-center">
            <SmuSearch
              value={searchDeletedProducts}
              onChange={(event) => setSearchDeletedProducts(event.target.value)}
            />
          </div>
        </Modal.Header>
        <Modal.Body>
          <SmuTable
            columns={deletedProductsColumns}
            data={filteredDeletedProducts}
            loading={loadingDeletedProducts}
            defaultSortFieldId="PRODUCTO"
            defaultSortAsc={false}
          />
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary" onClick={() => setShowDeletedModal(false)}>
            Cerrar
          </button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default ProductPromotionsTable;
